import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { QuoteModal } from "@/components/QuoteModal";
import { Ship, Package, Warehouse, Search, Shield, Clock, Globe, CheckCircle, TrendingUp, Award, Plane, Truck, FileText, BarChart3, Box, Anchor } from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { toast } from "sonner";
import heroImage from "@/assets/hero-port.jpg";
import bannerShipping from "@/assets/banner-shipping.jpg";
import globalNetwork from "@/assets/global-network.jpg";
import airFreight from "@/assets/air-freight.jpg";
import projectCargo from "@/assets/project-cargo.jpg";
import customsImage from "@/assets/customs.jpg";

import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";

const Index = () => {
  const navigate = useNavigate();
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState("");

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [Autoplay({ delay: 5000 })]);

  const handleTracking = (e: React.FormEvent) => {
    e.preventDefault();
    if (trackingNumber.trim()) {
      toast.success("Redirecting to tracking details...");
      navigate(`/tracking?id=${trackingNumber}`);
    }
  };

  const slides = [
    {
      image: heroImage,
      title: "Global shipping.",
      subtitle: "World-class delivery.",
      description: "End-to-end shipping, tracking and warehousing with secure, compliant operations from our corporate headquarters in Costa Mesa, California."
    },
    {
      image: bannerShipping,
      title: "Strategic Logistics Hubs.",
      subtitle: "Connecting Continents.",
      description: "Unparalleled coverage across the globe. ISO 9001 certified excellence at every port."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar onQuoteClick={() => setQuoteModalOpen(true)} />

      {/* Hero Slider */}
      <section className="relative h-[90vh] mt-20 overflow-hidden" ref={emblaRef}>
        <div className="flex h-full">
          {slides.map((slide, index) => (
            <div key={index} className="relative flex-[0_0_100%] h-full flex items-center justify-center">
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-10000 hover:scale-110"
                style={{ backgroundImage: `url(${slide.image})` }}
              >
                <div className="absolute inset-0 bg-gradient-to-b from-maritime-dark/80 via-maritime-dark/40 to-maritime-dark/90" />
              </div>

              <div className="relative z-10 container mx-auto px-4 text-center">
                <div className="max-w-3xl mx-auto animate-fade-in pt-8">
                  <h1 className="text-4xl md:text-6xl font-bold text-primary-foreground mb-4 leading-tight">
                    {slide.title}
                    <span className="block text-accent mt-1">{slide.subtitle}</span>
                  </h1>
                  <p className="text-base md:text-lg text-primary-foreground/90 mb-8 max-w-2xl mx-auto leading-relaxed">
                    {slide.description}
                  </p>

                  <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
                    <Button
                      size="lg"
                      className="text-lg px-8 h-12 shadow-xl hover-scale bg-primary text-primary-foreground font-bold"
                      onClick={() => setQuoteModalOpen(true)}
                    >
                      Request Exclusive Quote
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      className="text-lg px-8 h-12 bg-white/10 backdrop-blur-md border-white/30 text-white hover:bg-white/20 hover-scale shadow-xl font-bold"
                      asChild
                    >
                      <NavLink to="/tracking">Track Shipment</NavLink>
                    </Button>
                  </div>

                  {/* Quick Track Form - Restored to Center and Visibility */}
                  <div className="max-w-xl mx-auto animate-fade-in-up">
                    <form onSubmit={handleTracking} className="bg-background/95 backdrop-blur-sm p-1.5 rounded-lg shadow-2xl flex gap-2 border border-white/10">
                      <Input
                        placeholder="Enter Booking ID (e.g. CM-12345)"
                        value={trackingNumber}
                        onChange={(e) => setTrackingNumber(e.target.value)}
                        className="flex-1 border-0 focus-visible:ring-0 bg-transparent text-base h-10"
                      />
                      <Button type="submit" variant="default" className="px-6 h-10 rounded font-bold">
                        <Search className="h-4 w-4 mr-2" />
                        Track Now
                      </Button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Slider Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 z-30">
          {slides.map((_, index) => (
            <div key={index} className="h-1 w-10 bg-white/30 rounded-full" />
          ))}
        </div>
      </section>




      {/* Trust Strip */}
      <section className="py-8 bg-secondary border-y border-border">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-8 text-center">
            <div className="flex items-center space-x-2">
              <Shield className="h-5 w-5 text-primary" />
              <span className="text-sm font-medium">ISO 9001 Certified</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-primary" />
              <span className="text-sm font-medium">FMC Licensed</span>
            </div>
            <div className="flex items-center space-x-2">
              <Globe className="h-5 w-5 text-primary" />
              <span className="text-sm font-medium">120+ Partner Ports Worldwide</span>
            </div>
            <div className="flex items-center space-x-2">
              <Clock className="h-5 w-5 text-primary" />
              <span className="text-sm font-medium">24/7 Operations Support</span>
            </div>
          </div>
        </div>
      </section>

      {/* Core Services - Comprehensive */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <header className="text-center mb-16 animate-fade-in">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Complete Logistics Services</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              End-to-end supply chain solutions from USA to every corner of the world. ISO 9001 certified operations with real-time visibility.
            </p>
          </header>

          {/* Primary Services */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12 max-w-4xl mx-auto">
            <Card className="hover:shadow-lg transition-all border-2 hover:border-primary hover-scale animate-fade-in">
              <CardContent className="p-8">
                <Ship className="h-12 w-12 text-primary mb-4" />
                <h3 className="text-2xl font-bold mb-3">Ocean Freight</h3>
                <p className="text-muted-foreground mb-4">
                  Full container load (FCL) and less than container load (LCL) services with global coverage, competitive rates, and door-to-door delivery options.
                </p>
                <ul className="text-sm text-muted-foreground mb-6 space-y-2">
                  <li className="flex items-start"><CheckCircle className="h-4 w-4 text-primary mr-2 mt-0.5 flex-shrink-0" />FCL & LCL worldwide shipping</li>
                  <li className="flex items-start"><CheckCircle className="h-4 w-4 text-primary mr-2 mt-0.5 flex-shrink-0" />120+ partner ports globally</li>
                  <li className="flex items-start"><CheckCircle className="h-4 w-4 text-primary mr-2 mt-0.5 flex-shrink-0" />Real-time vessel tracking</li>
                  <li className="flex items-start"><CheckCircle className="h-4 w-4 text-primary mr-2 mt-0.5 flex-shrink-0" />Documentation & compliance support</li>
                </ul>
                <Button variant="outline" asChild className="hover-scale w-full">
                  <NavLink to="/ocean-freight">Explore Ocean Freight</NavLink>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all border-2 hover:border-primary hover-scale animate-fade-in">
              <CardContent className="p-8">
                <Warehouse className="h-12 w-12 text-primary mb-4" />
                <h3 className="text-2xl font-bold mb-3">Warehousing & Distribution</h3>
                <p className="text-muted-foreground mb-4">
                  Secure storage facilities with inventory management, value-added services, temperature-controlled options, and 24/7 CCTV monitoring.
                </p>
                <ul className="text-sm text-muted-foreground mb-6 space-y-2">
                  <li className="flex items-start"><CheckCircle className="h-4 w-4 text-primary mr-2 mt-0.5 flex-shrink-0" />50,000+ sqm warehouse space</li>
                  <li className="flex items-start"><CheckCircle className="h-4 w-4 text-primary mr-2 mt-0.5 flex-shrink-0" />WMS & real-time inventory tracking</li>
                  <li className="flex items-start"><CheckCircle className="h-4 w-4 text-primary mr-2 mt-0.5 flex-shrink-0" />Cross-docking & fulfilment</li>
                  <li className="flex items-start"><CheckCircle className="h-4 w-4 text-primary mr-2 mt-0.5 flex-shrink-0" />Bonded & temperature-controlled zones</li>
                </ul>
                <Button variant="outline" asChild className="hover-scale w-full">
                  <NavLink to="/warehousing">View Warehousing Services</NavLink>
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Additional Services */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="hover:shadow-lg transition-all hover:border-primary hover-scale animate-fade-in">
              <CardContent className="p-6">
                <Plane className="h-10 w-10 text-primary mb-3" />
                <h4 className="text-xl font-bold mb-2">Air Freight</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Express air cargo services for time-sensitive shipments with door-to-door delivery and customs clearance.
                </p>
                <Button variant="ghost" asChild className="hover-scale p-0 h-auto">
                  <NavLink to="/services">Learn More →</NavLink>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all hover:border-primary hover-scale animate-fade-in">
              <CardContent className="p-6">
                <Truck className="h-10 w-10 text-primary mb-3" />
                <h4 className="text-xl font-bold mb-2">Intermodal Transport</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Seamless road and rail transport connecting Athens to all European destinations with full tracking.
                </p>
                <Button variant="ghost" asChild className="hover-scale p-0 h-auto">
                  <NavLink to="/services">Learn More →</NavLink>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all hover:border-primary hover-scale animate-fade-in">
              <CardContent className="p-6">
                <FileText className="h-10 w-10 text-primary mb-3" />
                <h4 className="text-xl font-bold mb-2">Customs Brokerage</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Expert customs clearance, documentation, HS classification, and compliance support across all jurisdictions.
                </p>
                <Button variant="ghost" asChild className="hover-scale p-0 h-auto">
                  <NavLink to="/services">Learn More →</NavLink>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-all hover:border-primary hover-scale animate-fade-in">
              <CardContent className="p-6">
                <Box className="h-10 w-10 text-primary mb-3" />
                <h4 className="text-xl font-bold mb-2">Project Cargo</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Heavy lift and oversized cargo handling with specialized equipment, route surveys, and engineering support.
                </p>
                <Button variant="ghost" asChild className="hover-scale p-0 h-auto">
                  <NavLink to="/services">Learn More →</NavLink>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Industry Solutions */}
      <section className="py-20 bg-secondary">
        <div className="container mx-auto px-4">
          <header className="text-center mb-16 animate-fade-in">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Industry-Specific Solutions</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Tailored logistics expertise for specialized industries with compliance and temperature-controlled capabilities
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-background rounded-lg p-6 hover:shadow-lg transition-all hover-scale animate-fade-in">
              <Shield className="h-8 w-8 text-primary mb-3" />
              <h3 className="text-lg font-semibold mb-2">Pharmaceutical & Healthcare</h3>
              <p className="text-sm text-muted-foreground">GDP-compliant cold chain, temperature mapping, and pharma-grade warehousing.</p>
            </div>

            <div className="bg-background rounded-lg p-6 hover:shadow-lg transition-all hover-scale animate-fade-in">
              <Truck className="h-8 w-8 text-primary mb-3" />
              <h3 className="text-lg font-semibold mb-2">Automotive</h3>
              <p className="text-sm text-muted-foreground">RoRo services, finished vehicles, spare parts logistics with secure storage.</p>
            </div>

            <div className="bg-background rounded-lg p-6 hover:shadow-lg transition-all hover-scale animate-fade-in">
              <BarChart3 className="h-8 w-8 text-primary mb-3" />
              <h3 className="text-lg font-semibold mb-2">Retail & E-commerce</h3>
              <p className="text-sm text-muted-foreground">Fast fulfilment, cross-docking, returns management, and last-mile delivery.</p>
            </div>

            <div className="bg-background rounded-lg p-6 hover:shadow-lg transition-all hover-scale animate-fade-in">
              <Anchor className="h-8 w-8 text-primary mb-3" />
              <h3 className="text-lg font-semibold mb-2">Energy & Heavy Machinery</h3>
              <p className="text-sm text-muted-foreground">Breakbulk, heavy lift, project cargo with engineering and route planning.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-secondary">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Why Choose Costal Meridian</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Corporate headquarters in California with a commitment to global excellence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Operational Excellence</h3>
              <p className="text-muted-foreground">Single point of accountability for all your logistics needs</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Real-time Visibility</h3>
              <p className="text-muted-foreground">API tracking and instant shipment updates at your fingertips</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">24/7 Global Support</h3>
              <p className="text-muted-foreground">Dedicated customer success teams available around the clock</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Client-advantaged Rates</h3>
              <p className="text-muted-foreground">Tailored end-to-end solutions with competitive pricing</p>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Banner */}
      <section className="py-16 bg-gradient-to-r from-maritime-dark to-maritime-light text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div className="animate-fade-in">
              <TrendingUp className="h-12 w-12 text-accent mx-auto mb-4" />
              <div className="text-4xl font-bold mb-2">25+</div>
              <div className="text-sm opacity-90">Years of Excellence</div>
            </div>
            <div className="animate-fade-in">
              <Ship className="h-12 w-12 text-accent mx-auto mb-4" />
              <div className="text-4xl font-bold mb-2">50,000+</div>
              <div className="text-sm opacity-90">Containers Shipped Annually</div>
            </div>
            <div className="animate-fade-in">
              <Globe className="h-12 w-12 text-accent mx-auto mb-4" />
              <div className="text-4xl font-bold mb-2">120+</div>
              <div className="text-sm opacity-90">Partner Ports Worldwide</div>
            </div>
            <div className="animate-fade-in">
              <Award className="h-12 w-12 text-accent mx-auto mb-4" />
              <div className="text-4xl font-bold mb-2">99.8%</div>
              <div className="text-sm opacity-90">On-Time Delivery Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Showcases with Images */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          {/* Air Freight Showcase */}
          <article className="relative rounded-xl overflow-hidden shadow-2xl mb-12 animate-fade-in">
            <img src={airFreight} alt="Air Freight Services" className="w-full h-[400px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-maritime-dark/90 to-maritime-dark/70 flex items-center">
              <div className="container mx-auto px-8 text-primary-foreground">
                <Plane className="h-12 w-12 mb-4 text-accent" />
                <h2 className="text-4xl md:text-5xl font-bold mb-4">Air Freight Express</h2>
                <p className="text-xl mb-6 max-w-2xl">
                  Time-sensitive shipments delivered globally with priority handling. Direct flights from Athens International Airport to major hubs worldwide.
                </p>
                <div className="flex gap-4 flex-wrap">
                  <Button size="lg" variant="secondary" onClick={() => setQuoteModalOpen(true)} className="hover-scale">
                    Get Air Freight Quote
                  </Button>
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary hover-scale" asChild>
                    <NavLink to="/services">View All Air Services</NavLink>
                  </Button>
                </div>
              </div>
            </div>
          </article>

          {/* Project Cargo Showcase */}
          <article className="relative rounded-xl overflow-hidden shadow-2xl mb-12 animate-fade-in">
            <img src={projectCargo} alt="Project Cargo Solutions" className="w-full h-[400px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-maritime-dark/90 to-maritime-dark/70 flex items-center">
              <div className="container mx-auto px-8 text-primary-foreground">
                <Box className="h-12 w-12 mb-4 text-accent" />
                <h2 className="text-4xl md:text-5xl font-bold mb-4">Project & Heavy Cargo Expertise</h2>
                <p className="text-xl mb-6 max-w-2xl">
                  Specialized handling for oversized and heavy cargo. Engineering surveys, route planning, and multimodal coordination for complex projects.
                </p>
                <div className="flex gap-4 flex-wrap">
                  <Button size="lg" variant="secondary" onClick={() => setQuoteModalOpen(true)} className="hover-scale">
                    Discuss Your Project
                  </Button>
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary hover-scale" asChild>
                    <NavLink to="/services">View Project Cargo Services</NavLink>
                  </Button>
                </div>
              </div>
            </div>
          </article>

          {/* Customs Brokerage Showcase */}
          <article className="relative rounded-xl overflow-hidden shadow-2xl animate-fade-in">
            <img src={customsImage} alt="Customs Brokerage Services" className="w-full h-[400px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-maritime-dark/90 to-maritime-dark/70 flex items-center">
              <div className="container mx-auto px-8 text-primary-foreground">
                <FileText className="h-12 w-12 mb-4 text-accent" />
                <h2 className="text-4xl md:text-5xl font-bold mb-4">Seamless Customs Clearance</h2>
                <p className="text-xl mb-6 max-w-2xl">
                  Expert brokerage services with electronic filing, HS classification, duty optimization, and full compliance support across EU and international borders.
                </p>
                <div className="flex gap-4 flex-wrap">
                  <Button size="lg" variant="secondary" onClick={() => setQuoteModalOpen(true)} className="hover-scale">
                    Contact Customs Team
                  </Button>
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary hover-scale" asChild>
                    <NavLink to="/services">Learn About Customs Services</NavLink>
                  </Button>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* Global Network */}
      <section className="py-20 bg-secondary">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-fade-in">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Our Global Network</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Connected to major ports across six continents from our California headquarters
            </p>
          </div>
          <div className="max-w-5xl mx-auto animate-fade-in">
            <img src={globalNetwork} alt="Global Shipping Network" className="w-full rounded-xl shadow-xl" />
          </div>
        </div>
      </section>

      {/* Client Success Stories */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <header className="text-center mb-16 animate-fade-in">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">What Our Clients Say</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Trusted by leading businesses across USA and worldwide
            </p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                quote: "Costal Meridian has transformed our supply chain. Their team handles our transpacific routes flawlessly, reducing transit times by 30%.",
                author: "Maria Peterson",
                role: "Supply Chain Director",
                company: "PacWest Foods Corp"
              },
              {
                quote: "The team provides exceptional service for our APAC operations. Real-time tracking and proactive communication sets them apart.",
                author: "Sean Tanaka",
                role: "Logistics Manager",
                company: "Apex Electronics Ltd"
              },
              {
                quote: "From ocean freight to customs clearance, Costal Meridian is our single point of contact. Their expertise in US customs regulations is invaluable.",
                author: "Klaus Weber",
                role: "Operations VP",
                company: "German Auto Parts GmbH"
              }
            ].map((testimonial, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-all hover-scale animate-fade-in">
                <CardContent className="p-8">
                  <div className="flex mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Award key={i} className="h-5 w-5 text-yellow-500 fill-yellow-500" />
                    ))}
                  </div>
                  <p className="text-muted-foreground mb-6 italic">"{testimonial.quote}"</p>
                  <div>
                    <p className="font-bold">{testimonial.author}</p>
                    <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                    <p className="text-sm text-primary">{testimonial.company}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button variant="outline" asChild className="hover-scale">
              <NavLink to="/reviews">Read All Reviews</NavLink>
            </Button>
          </div>
        </div>
      </section>

      {/* Service Coverage Section */}
      <section className="py-20 bg-secondary">
        <div className="container mx-auto px-4">
          <div className="bg-background rounded-xl p-8 max-w-5xl mx-auto animate-fade-in">
            <h3 className="text-2xl font-bold text-center mb-8">Our Service Coverage</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { region: "Europe", ports: "45+ ports", highlight: "Regional Partners" },
                { region: "Asia Pacific", ports: "38+ ports", highlight: "Regional Partners" },
                { region: "Americas", ports: "30+ ports", highlight: "USA HQ" },
                { region: "Middle East & Africa", ports: "20+ ports", highlight: "Growing network" },
              ].map((region, idx) => (
                <div key={idx} className="text-center p-4 border border-border rounded-lg">
                  <Globe className="h-8 w-8 text-primary mx-auto mb-2" />
                  <p className="font-bold">{region.region}</p>
                  <p className="text-sm text-muted-foreground">{region.ports}</p>
                  <p className="text-xs text-primary font-medium">{region.highlight}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-primary-foreground animate-fade-in">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Ship with Confidence?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            Contact our USA office today for a personalized quote and experience the Costal Meridian difference.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              variant="secondary"
              className="text-lg px-8 hover-scale"
              onClick={() => setQuoteModalOpen(true)}
            >
              Get Your Quote
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="text-lg px-8 border-white text-white hover:bg-white hover:text-primary hover-scale"
              asChild
            >
              <NavLink to="/contact">Contact Our Team</NavLink>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <QuoteModal open={quoteModalOpen} onOpenChange={setQuoteModalOpen} />
    </div>
  );
};

export default Index;
